/**
 * Parse a cron expression and extract schedule configuration
 * Cron format: minute hour day-of-month month day-of-week
 * Example: "00 09 * * 1,2,3,4,5,6" means 9:00 AM on Mon-Sat
 */

interface ScheduleConfig {
  cadence: 'daily' | 'weekly' | 'monthly';
  time: string;
  days?: number[];
  day?: number[];
}

export function parseCronExpression(cronExpression: string): ScheduleConfig | null {
  if (!cronExpression || typeof cronExpression !== 'string') {
    return null;
  }

  const parts = cronExpression.trim().split(/\s+/);

  if (parts.length < 5) {
    console.warn('[cronParser] Invalid cron expression - less than 5 parts:', cronExpression);
    return null;
  }

  const [minute, hour, dayOfMonth, , dayOfWeek] = parts;

  // Parse time (hour:minute)
  const hourNum = parseInt(hour, 10);
  const minuteNum = parseInt(minute, 10);

  if (isNaN(hourNum) || isNaN(minuteNum)) {
    return null;
  }

  const time = `${hourNum.toString().padStart(2, '0')}:${minuteNum.toString().padStart(2, '0')}`;

  // Determine cadence based on cron pattern
  // Monthly: specific day of month (e.g., "5" or "1,15")
  if (dayOfMonth !== '*') {
    const days = dayOfMonth
      .split(',')
      .map(d => parseInt(d.trim(), 10))
      .filter(d => !isNaN(d));
    return {
      cadence: 'monthly',
      time,
      day: days.length > 0 ? days : [1],
    };
  }

  // Weekly or Daily: based on day of week
  if (dayOfWeek === '*') {
    // Every day
    return {
      cadence: 'daily',
      time,
      days: [1, 2, 3, 4, 5, 6, 7], // All days
    };
  }

  // Parse day of week (1=Monday, 7=Sunday in cron, but also supports 0=Sunday)
  const daysOfWeek = dayOfWeek
    .split(',')
    .map(d => {
      const day = parseInt(d.trim(), 10);
      // Convert 0 (Sunday in some cron formats) to 7
      return day === 0 ? 7 : day;
    })
    .filter(d => !isNaN(d) && d >= 1 && d <= 7);

  if (daysOfWeek.length === 0) {
    return {
      cadence: 'daily',
      time,
      days: [1, 2, 3, 4, 5, 6, 7],
    };
  }

  // If all 7 days are selected, it's daily
  if (daysOfWeek.length === 7) {
    return {
      cadence: 'daily',
      time,
      days: daysOfWeek.sort((a, b) => a - b),
    };
  }

  // Otherwise it's weekly with specific days
  return {
    cadence: 'weekly' as const,
    time,
    days: daysOfWeek.sort((a, b) => a - b),
  };
}

/**
 * Generate a cron expression from schedule configuration
 * This is the inverse of parseCronExpression
 */
export function generateCronExpression(
  cadence: string,
  scheduleConfig?: {
    time?: string;
    days?: number[];
    day?: number | number[];
  }
): string {
  const time = scheduleConfig?.time ?? '09:00';
  const [hour = '9', minute = '0'] = time.split(':');

  switch (cadence) {
    case 'daily': {
      // For daily cadence, always use all days (*) regardless of days array
      // The days array is only used for weekly cadence
      return `${minute} ${hour} * * *`;
    }
    case 'weekly': {
      const days = scheduleConfig?.days;
      if (days && days.length > 0) {
        const cronDays = days.join(',');
        return `${minute} ${hour} * * ${cronDays}`;
      }
      return `${minute} ${hour} * * 1`;
    }
    case 'monthly': {
      const day = scheduleConfig?.day;
      let dayOfMonth = 1;

      if (Array.isArray(day)) {
        // If it's an array, join with commas
        const dayStr = day.join(',');
        return `${minute} ${hour} ${dayStr} * *`;
      } else if (typeof day === 'number') {
        dayOfMonth = day;
      }

      return `${minute} ${hour} ${dayOfMonth} * *`;
    }
    default:
      return `${minute} ${hour} * * 1-5`;
  }
}
